import type React from "react";
import { uiVar } from "./uiTheme";

// Dialog and menu styling — the single source of truth for hand-rolled dialogs.
// Originally the Acrylic Glass theme (June 2026, see
// docs/superpowers/specs/2026-06-11-acrylic-glass-theme-design.md); since
// September 2026 every value is a `var(--ui-*)` reference resolved by the theme
// chosen under File > Theme (src/theme/uiTheme.ts), Acrylic Glass by default.
// Every dialog uses these tokens/styles, either through the global.css classes
// (DialogWrapper-based dialogs) or by importing this module (hand-rolled ones).
// Buttons and close glyphs should use the CSS classes .btn-acrylic-primary /
// .btn-acrylic-secondary / .acrylic-close-btn so hover states stay in CSS.

export const acrylicTokens = {
  font: uiVar("font"),
  accent: uiVar("accent"),
  accentHover: uiVar("accentHover"),
  textPrimary: uiVar("textPrimary"),
  textSecondary: uiVar("textSecondary"),
  textMuted: uiVar("textMuted"),
  overlayDim: uiVar("overlayDim"),
  surface: uiVar("surface"),
  surfaceBlur: uiVar("surfaceBlur"),
  surfaceBorder: uiVar("surfaceBorder"),
  surfaceRadius: uiVar("surfaceRadius"),
  surfaceShadow: uiVar("surfaceShadow"),
  titleBg: uiVar("titleBg"),
  titleText: uiVar("titleText"),
  titleBorder: uiVar("titleBorder"),
  card: uiVar("card"),
  cardBorder: uiVar("cardBorder"),
  cardRadius: uiVar("cardRadius"),
  controlBg: uiVar("controlBg"),
  controlBorder: uiVar("controlBorder"),
  controlRadius: uiVar("controlRadius"),
  tableBg: uiVar("tableBg"),
  tableBorder: uiVar("tableBorder"),
  tableHeaderBg: uiVar("tableHeaderBg"),
  rowDivider: uiVar("rowDivider"),
  selection: uiVar("selection"),
  rowHover: uiVar("rowHover"),
  footerBg: uiVar("footerBg"),
  footerBorder: uiVar("footerBorder"),
  separator: uiVar("separator"),
} as const;

const t = acrylicTokens;

export interface AcrylicDialogSurfaceOpts {
  width: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  /** When false the dialog is fixed-size (no resize handle). Default true. */
  resizable?: boolean;
}

/** The frosted dialog card itself, statically centred via calc() so resizing
 *  grows it from the top-left anchor instead of re-centering each frame (the
 *  drag handle slips out from under the cursor otherwise — see
 *  GraphChannelSelectionDialog history). */
export function acrylicDialogSurface(opts: AcrylicDialogSurfaceOpts): React.CSSProperties {
  const { width, height, minWidth, minHeight, resizable = true } = opts;
  const halfH = Math.round((height ?? 400) / 2);
  const halfW = Math.round(width / 2);
  return {
    position: "absolute",
    // Dynamic viewport units (dvh/dvw) track the *visible* viewport, which
    // shrinks as mobile browser chrome (iOS Safari toolbars) retracts. Plain
    // vh/vw resolve against the large viewport there, so a fixed-height dialog
    // overshoots the visible area and the footer falls off-screen on iPad.
    // On desktop dvh === vh, so this is a no-op.
    top: `max(2.5dvh, calc(50dvh - ${halfH}px))`,
    left: `max(2.5dvw, calc(50dvw - ${halfW}px))`,
    background: t.surface,
    backdropFilter: t.surfaceBlur,
    WebkitBackdropFilter: t.surfaceBlur,
    border: t.surfaceBorder,
    borderRadius: t.surfaceRadius,
    width,
    ...(height != null ? { height } : {}),
    // Clamp the min constraints against the viewport: on a short/narrow window
    // (e.g. the graph popup in Vivaldi, or iPad Safari where browser chrome
    // shrinks the viewport) a fixed minHeight/minWidth would otherwise win over
    // the 95dvh/95dvw max and push the footer off-screen, since CSS min-*
    // overrides max-*. Uses dvh/dvw so the clamp matches the visible viewport.
    ...(minWidth != null ? { minWidth: `min(${minWidth}px, 95dvw)` } : {}),
    ...(minHeight != null ? { minHeight: `min(${minHeight}px, 95dvh)` } : {}),
    maxWidth: "95dvw",
    maxHeight: "95dvh",
    boxShadow: t.surfaceShadow,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...(resizable ? { resize: "both" as const } : {}),
    fontFamily: t.font,
    color: t.textPrimary,
  };
}

export const acrylicStyles = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: t.overlayDim,
    // Above the menu bar (z 1100) so modal dims cover it — see .dialog-overlay.
    zIndex: 1200,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 24px",
    // Transparent under Acrylic Glass; a title band under the themes that have
    // one (Classic Desktop gradient, Chromalloy charcoal + crimson stripe, …).
    background: t.titleBg,
    borderBottom: t.titleBorder,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: t.titleText,
    letterSpacing: "-0.01em",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    color: t.textMuted,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: t.controlRadius,
    lineHeight: 1,
    minWidth: "auto",
  },
  body: {
    padding: "4px 24px 20px",
    display: "flex",
    flexDirection: "column" as const,
    gap: 16,
    overflow: "auto" as const,
    flex: 1,
  },
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: t.textSecondary,
  },
  card: {
    background: t.card,
    borderRadius: t.cardRadius,
    padding: 14,
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    border: t.cardBorder,
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  fieldGroup: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
  },
  field: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  fieldNarrow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
    flex: "0 0 130px",
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: t.textSecondary,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: t.textSecondary,
    minWidth: 100,
    flexShrink: 0,
  },
  select: {
    flex: 1,
    fontSize: 13,
    padding: "7px 10px",
    borderRadius: t.controlRadius,
    border: t.controlBorder,
    background: t.controlBg,
    color: t.textPrimary,
    outline: "none",
    fontFamily: "inherit",
  },
  input: {
    flex: 1,
    fontSize: 13,
    padding: "7px 10px",
    borderRadius: t.controlRadius,
    border: t.controlBorder,
    background: t.controlBg,
    color: t.textPrimary,
    outline: "none",
    fontFamily: "inherit",
  },
  tableWrap: {
    borderRadius: t.cardRadius,
    border: t.tableBorder,
    background: t.tableBg,
    overflow: "hidden" as const,
  },
  btnRow: {
    display: "flex",
    gap: 8,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    padding: "12px 24px",
    borderTop: t.footerBorder,
    background: t.footerBg,
  },
  // Prefer className="btn-acrylic-primary" / "btn-acrylic-secondary" (CSS
  // :hover); these objects exist for places that must stay inline.
  btnPrimary: {
    fontSize: 13,
    fontWeight: 500,
    padding: "8px 20px",
    borderRadius: t.controlRadius,
    border: uiVar("btnPrimaryBorder"),
    background: uiVar("btnPrimaryBg"),
    color: uiVar("btnPrimaryText"),
    cursor: "pointer",
    fontFamily: "inherit",
    minWidth: 80,
    boxShadow: uiVar("btnPrimaryShadow"),
  },
  btnSecondary: {
    fontSize: 13,
    fontWeight: 500,
    padding: "8px 20px",
    borderRadius: t.controlRadius,
    border: uiVar("btnSecondaryBorder"),
    background: uiVar("btnSecondaryBg"),
    color: uiVar("btnSecondaryText"),
    cursor: "pointer",
    fontFamily: "inherit",
    minWidth: 80,
  },
  radio: {
    marginRight: 4,
  },
  radioLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: t.textSecondary,
    marginRight: 16,
    cursor: "pointer",
  },
} as const satisfies Record<string, React.CSSProperties>;
